import { Modal } from 'antd';
import {
  useGetFieldsGeometryQuery,
  useGetTaskQuery,
  useGetTaskRoutesQuery,
} from 'app/api';
import {
  selectSelectedCompanyGroupId,
  selectSelectedCompanyId,
} from 'app/containers/Company/slice/selectors';
import { fieldSubmenuEnum } from 'app/containers/Field/slice/types';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { useMapSlice } from 'app/containers/Map/slice';
import { selectSelectedSeasonId } from 'app/containers/Season/slice/selectors';
import { useTaskSlice } from 'app/containers/Task/slice';
import {
  selectSingleFieldTaskListState,
  selectTaskDetailsAreaByCompanies,
  selectTaskDetailsAreaByCultures,
  selectTaskListState,
} from 'app/containers/Task/slice/selectors';
import { detailsViewTypeEnum } from 'app/containers/Task/slice/types';
import { RouteConstants } from 'app/routes';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { MenuInfo } from 'types';
import { getTaskListFiltersSearchQuery } from 'utils/helpers';
import { useResponsiveQuery } from 'utils/responsiveQuery/useResponsiveQuery';

export const useTaskDetails = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const history = useHistory();

  const { mobileOrTablet } = useResponsiveQuery();

  const params = useParams<{
    taskId: string;
    // field id arrives only if details page was opened from field tasks
    fieldId?: string;
  }>();

  const taskId = Number.isNaN(Number(params.taskId))
    ? undefined
    : Number(params.taskId);

  const fieldId = Number.isNaN(Number(params.fieldId))
    ? undefined
    : Number(params.fieldId);

  const [selectedKey, setSelectedKey] = useState(detailsViewTypeEnum.map);

  const { actions } = useTaskSlice();
  const { actions: mapActions } = useMapSlice();

  const envId = useSelector(selectSelectedEnvId) as number;
  const seasonId = useSelector(selectSelectedSeasonId) as number;
  const companyId = useSelector(selectSelectedCompanyId);
  const companyGroupId = useSelector(selectSelectedCompanyGroupId);
  const { filters: taskListFilters, currentPage: taskListPage } = useSelector(
    selectTaskListState,
  );

  const { data: taskRoutes } = useGetTaskRoutesQuery({
    envId,
    taskId: taskId as number,
  });

  useGetFieldsGeometryQuery({
    envId,
    seasonId,
    companyId: null,
    companyGroupId: null,
  });

  const isTaskRoutes = Boolean(taskRoutes?.length);

  const areaByCompanies = useSelector(selectTaskDetailsAreaByCompanies);
  const areaByCultures = useSelector(selectTaskDetailsAreaByCultures);

  const {
    filters: fieldTaskListFilters,
    currentPage: fieldTaskListPage,
  } = useSelector(selectSingleFieldTaskListState);

  const { currentData: task, isFetching: isTaskLoading } = useGetTaskQuery(
    { envId, taskId: taskId as number },
    { skip: !taskId },
  );

  useEffect(() => {
    if (taskId && envId && seasonId) {
      dispatch(
        actions.getTaskDetails({
          envId,
          taskId,
        }),
      );
      // Load all polygons on map
      dispatch(
        mapActions.onLoad({ envId, seasonId, companyId, companyGroupId }),
      );

      dispatch(actions.getTaskPlannedResources({ envId, taskId }));
      dispatch(actions.getTaskUsedResources({ envId, taskId }));
    }
  }, [
    taskId,
    actions,
    dispatch,
    mapActions,
    envId,
    seasonId,
    companyId,
    companyGroupId,
  ]);
  const [isNoteModalShown, setIsNoteModalShown] = useState(false);

  useEffect(() => {
    // If task created from recommendation - show modal with instructions
    if (
      !isTaskLoading &&
      task &&
      taskId === task.id &&
      task.note &&
      !isNoteModalShown &&
      // taskReferenceId have only tasks created from recommendation
      (task.taskReferenceId || task.status === 'RECOMMENDED')
    ) {
      setIsNoteModalShown(true);
      Modal.info({
        title: 'Užduotyje esanti instrukcija',
        content: task.note,
        style: { whiteSpace: 'pre-line' },
      });
    }
  }, [task, isNoteModalShown, isTaskLoading, taskId]);

  const onClickMenu = (clickedItem: MenuInfo) => {
    const key = clickedItem.key as detailsViewTypeEnum;
    setSelectedKey(key);
  };

  const onBack = () => {
    if (envId) {
      if (fieldId) {
        const fieldTaskListFiltersQuery = getTaskListFiltersSearchQuery({
          filters: fieldTaskListFilters,
          currentPage: fieldTaskListPage,
        });

        history.push(
          generatePath(RouteConstants.field.details, {
            envId,
            fieldId,
          }).concat(
            `?tab=${fieldSubmenuEnum.tasks}&${fieldTaskListFiltersQuery}`,
          ),
        );
      } else {
        const taskListFiltersQuery = getTaskListFiltersSearchQuery({
          filters: taskListFilters,
          currentPage: taskListPage,
        });
        history.push(
          generatePath(RouteConstants.task.list, {
            envId,
          }).concat(`?${taskListFiltersQuery}`),
        );
      }
    }
  };

  return {
    theme,
    areaByCultures,
    areaByCompanies,
    selectedKey,
    mobileOrTablet,
    onBack,
    onClickMenu,
    taskId,
    task,
    isTaskRoutes,
  };
};
